import { I18n } from '@aws-amplify/core';
import { Button, Col, Form, Row, Skeleton } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import { CustomReportStepKeyDTO, IntegratedErgonomicToolDTO } from '@/components/ui/Inputs/types/response/customReport';
import { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import { useApplicationContext } from '@/context/v1/Application/context';

import { useGetBeraStepKeyResult } from '@/hooks/useGetBeraStepKeyResult';

import { useFrequencies, useStressLevels, useTotalTaskDurations } from '../../../hooks/hooks';
import { StepKeyService } from '../../../services/stepKeyService';
import { useStepKeyAutosave, useStepKeyInitialLoad, useStepKeyPopulate, useStepKeyTaskRpn } from './_hooks';

import { ActionPlanIcon } from '@/assets/icons/ActionPlanIcon';
import { ActionPlanModal } from '@/components/ActionPlan';
import { RiskBadge } from '@/components/views/BeraJob/components/RiskBadge';
import { CollapseCheck } from './CollapseCheck';
import { Frequencies } from './Frequencies';
import { JobElement } from './JobElement';
import { PopulateSwitch } from './PopulateSwitch';
import { StressLevels } from './StressLevels';
import { TaskRPN } from './TaskRPN';
import { ToolCards } from './ToolCards';
import { TotalTaskDurations } from './TotalTaskDuration';
import { DropdownCol } from './styles';

const { useFormInstance, useWatch } = Form;

interface StepProps {
	stepId: string;
	emptyChecked: boolean;
	file: FileForCustomReport;
	stepKey: CustomReportStepKeyDTO;
	beraReportId: string;
	integratedTools: IntegratedErgonomicToolDTO[];
	onAnyStepKeyCheck: (stepKeyId: string, value: boolean) => void;
	onAutoSave(stepId: string, stepKeyId: string, isEmpty?: boolean): Promise<void>;
	workstation_id: string;
	currentBeraStepResultId: string | null;
}

export const StepKey = React.memo(function StepKey({
	file,
	stepId,
	stepKey,
	emptyChecked,
	beraReportId,
	integratedTools,
	onAnyStepKeyCheck,
	onAutoSave,
	workstation_id,
	currentBeraStepResultId
}: Readonly<StepProps>): JSX.Element {
	const { organization, company } = useApplicationContext();
	const { mutateAsync: getBeraStepKeyResult, isLoading: gettingBeraStepKeyResult } = useGetBeraStepKeyResult();
	const [openActionPlan, setOpenActionPlan] = useState(false);

	const [taskRPN, setTaskRPN] = useState<number>(0);
	const [checked, setChecked] = useState<boolean>(false);

	const isFirstRender = useRef<boolean>(true);
	const hasLoadedInitialData = useRef(false);
	const isSaving = useRef<boolean>(false);
	const debounceRef = useRef<NodeJS.Timeout>();
	const prevStressLevelId = useRef<string | undefined>(undefined);
	const prevFrequencyId = useRef<string | undefined>(undefined);
	const prevTotalTaskDurationId = useRef<string | undefined>(undefined);
	const prevJobElement = useRef<string | undefined>(undefined);
	const prevChecked = useRef<boolean>(false);

	const { setFieldValue, validateFields, getFieldValue } = useFormInstance();
	const [actionPlanId, setActionPlanId] = useState<string | null>(null);

	useStepKeyInitialLoad({
		organizationId: organization?.id,
		companyId: company?.id,
		beraReportId,
		stepKey,
		fileId: file.id,
		stepId,
		setFieldValue,
		getFieldValue,
		setTaskRPN,
		setActionPlanId,
		getBeraStepKeyResult,
		prevStressLevelId,
		prevFrequencyId,
		prevTotalTaskDurationId,
		prevJobElement,
		hasLoadedInitialData
	});

	const stressLevels = useStressLevels({
		organization_id: organization?.id,
		company_id: company?.id,
		step_key_id: stepKey.id
	});

	const totalTaskDurations = useTotalTaskDurations({
		organization_id: organization?.id,
		company_id: company?.id,
		step_key_id: stepKey.id
	});

	const frequencies = useFrequencies({
		organization_id: organization?.id,
		company_id: company?.id,
		step_key_id: stepKey.id
	});

	const stepKeyService = new StepKeyService(stepKey, stressLevels.data, frequencies.data, totalTaskDurations.data);

	const totalTaskDurationId = useWatch([file.id, stepId, stepKey.id, 'total_task_duration_id']);
	const stressLevelId = useWatch([file.id, stepId, stepKey.id, 'stress_level_id']);
	const frequencyId = useWatch([file.id, stepId, stepKey.id, 'frequency_id']);
	const currentTaskRpn = useWatch([file.id, stepId, stepKey.id, 'task_rpn']);
	const watchedStepKey = useWatch([file.id, stepId, stepKey.id, 'step_key']);

	const selectedStressLevel = stressLevels.data.find((stressLevel) => stressLevel.id === stressLevelId);

	const { populate, disabled, ergonomicToolMapper, handleSwitchChange, resetPopulate } = useStepKeyPopulate({
		integratedTools,
		fileId: file.id,
		organizationId: organization?.id,
		companyId: company?.id,
		stressLevels: stressLevels.data,
		stressLevelFieldPath: [file.id, stepId, stepKey.id, 'stress_level_id'],
		setFieldValue,
		validateFields
	});

	useStepKeyTaskRpn({
		stressLevelId,
		frequencyId,
		totalTaskDurationId,
		currentTaskRpn,
		stepKeyService,
		formPath: [file.id, stepId, stepKey.id, 'task_rpn'],
		setFieldValue,
		setTaskRPN
	});

	const jobElement = useWatch([file.id, stepId, stepKey.id, 'job_element']);

	useStepKeyAutosave({
		checked,
		beraReportId,
		gettingBeraStepKeyResult,
		stressLevelId,
		frequencyId,
		totalTaskDurationId,
		jobElement,
		stepId,
		stepKeyId: stepKey.id,
		onAutoSave,
		isFirstRender,
		hasLoadedInitialData,
		prevStressLevelId,
		prevFrequencyId,
		prevTotalTaskDurationId,
		prevJobElement,
		prevChecked,
		debounceRef,
		isSaving
	});

	useEffect(() => {
		const timer = setTimeout(() => {
			isFirstRender.current = false;
		}, 0);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (!hasLoadedInitialData.current) return;
		if (watchedStepKey === undefined) return;
		setChecked(!!watchedStepKey);
	}, [watchedStepKey]);

	function handleCheckStepKey(value: boolean) {
		if (emptyChecked) {
			return;
		}

		resetPopulate();
		setChecked(value);
		onAnyStepKeyCheck(stepKey.id, value);
	}

	const loading = stressLevels.isLoading || totalTaskDurations.isLoading || frequencies.isLoading;

	function handleAddActionPlan() {
		setOpenActionPlan(true);
	}

	function canCreateActionPlan() {
		return totalTaskDurationId || stressLevelId || frequencyId;
	}

	return (
		<Col xs={22}>
			<Form.Item name={[file.id, stepId, stepKey.id, 'step_key']}>
				{loading ? (
					<Skeleton.Input block active />
				) : (
					<CollapseCheck
						key={stepKey.id}
						value={checked}
						anyOtherChecked={emptyChecked}
						title={stepKey.description}
						onChange={handleCheckStepKey}
					>
						<Row justify="space-between" gutter={[64, 12]}>
							{checked && (
								<>
									<DropdownCol xs={24} sm={24} md={24} lg={12}>
										<Row align="bottom">
											<StressLevels
												key={stepKey.id}
												disabled={populate}
												isError={stressLevels.isError}
												stressLevels={stressLevels.data}
												isLoading={stressLevels.isLoading}
												isFetching={stressLevels.isFetching}
												fieldName={[file.id, stepId, stepKey.id, 'stress_level_id']}
												fieldScoreName={[file.id, stepId, stepKey.id, 'stress_level_score']}
											/>
										</Row>
										<Row align="bottom">
											<Frequencies
												key={stepKey.id}
												disabled={populate}
												stepKeyName={stepKey.name}
												isError={frequencies.isError}
												frequencies={frequencies.data}
												isLoading={frequencies.isLoading}
												isFetching={frequencies.isFetching}
												selectedStressLevel={selectedStressLevel}
												fieldName={[file.id, stepId, stepKey.id, 'frequency_id']}
												fieldScoreName={[file.id, stepId, stepKey.id, 'frequency_score']}
											/>
										</Row>
										<Row align="bottom">
											<TotalTaskDurations
												key={stepKey.id}
												disabled={populate}
												stepKeyName={stepKey.name}
												isError={totalTaskDurations.isError}
												isLoading={totalTaskDurations.isLoading}
												selectedStressLevel={selectedStressLevel}
												isFetching={totalTaskDurations.isFetching}
												totalTaskDurations={totalTaskDurations.data}
												fieldName={[file.id, stepId, stepKey.id, 'total_task_duration_id']}
												fieldScoreName={[
													file.id,
													stepId,
													stepKey.id,
													'total_task_duration_score'
												]}
											/>
										</Row>

										<PopulateSwitch
											integratedTools={integratedTools}
											disabled={disabled}
											populate={populate}
											onSwitchChange={handleSwitchChange}
										/>

										<ToolCards
											integratedTools={integratedTools}
											ergonomicTool={ergonomicToolMapper}
											populate={populate}
											fileId={file.id}
										/>
									</DropdownCol>
									<Col xs={24} sm={24} md={24} lg={12}>
										<Row>
											<JobElement fieldName={[file.id, stepId, stepKey.id, 'job_element']} />
										</Row>
										<Row justify="space-between" style={{ alignItems: 'center' }}>
											<Col
												style={{
													alignItems: 'center',
													justifyContent: 'center',
													textAlign: 'center'
												}}
											>
												<TaskRPN
													fieldName={[file.id, stepId, stepKey.id, 'task_rpn']}
													task_rpn={taskRPN}
												/>
											</Col>
											<Col>
												<Button
													icon={<ActionPlanIcon />}
													onClick={() => handleAddActionPlan()}
													disabled={!canCreateActionPlan()}
													style={{
														width: '100%',
														justifyContent: 'center',
														alignItems: 'center',
														display: 'flex'
													}}
												>
													{actionPlanId
														? I18n.get('Action plan')
														: I18n.get('Add action plan')}
												</Button>
											</Col>
										</Row>
									</Col>
									<ActionPlanModal
										open={openActionPlan}
										onClose={() => setOpenActionPlan(false)}
										table_origin="bera_step_key_results"
										table_origin_id={currentBeraStepResultId ?? ''}
										file_id={file.id}
										workstation_id={workstation_id}
										origin_name={stepKey.description}
										action_plan_id={actionPlanId}
										setActionPlanId={setActionPlanId}
										risk={<RiskBadge taskRPN={taskRPN} />}
									/>
								</>
							)}
						</Row>
					</CollapseCheck>
				)}
			</Form.Item>
		</Col>
	);
});
